/* eslint-disable @angular-eslint/prefer-on-push-component-change-detection */
/* eslint-disable @angular-eslint/prefer-signals */

import { Component, Input, type OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Tab } from '@sbzen/ng-cron';

import { getTimezoneOptions } from '../../helpers/timezone-helpers/get-timezone-options';
import { resolveDefaultTimezone } from '../../helpers/timezone-helpers/resolve-default-timezone';
import type { TimezoneOption } from '../../helpers/timezone-helpers/timezone-option.interface';
import { validateCronScheduledSync } from '../../helpers/validate-cron-scheduled-sync';
import { validateCronScheduledSyncTimezone } from '../../helpers/validate-cron-scheduled-sync-timezone';

const HOUR_OPTIONS = Array.from({ length: 24 }, (_, hour) => ({
  label: `${String(hour).padStart(2, '0')}:00`,
  value: hour,
}));

@Component({
  selector: 'app-cron-expression-edit-modal',
  standalone: false, // eslint-disable-line @angular-eslint/prefer-standalone
  styleUrl: './cron-expression-edit-modal.component.scss',
  templateUrl: './cron-expression-edit-modal.component.html',
})
export class CronExpressionEditModalComponent implements OnInit {
  public activeCronTab = Tab.DAY;
  public readonly cronTabs = [Tab.DAY];
  public readonly hourOptions = HOUR_OPTIONS;
  @Input() initialCronExpression?: string;
  @Input() initialTimezone?: string;
  public selectedHour = 0;
  public selectedTimezone = '';
  public readonly timezones: TimezoneOption[] = getTimezoneOptions();
  public validationError = '';
  public value = '0 0 * * *';

  constructor(public modal: NgbActiveModal) {}

  public close(): void {
    this.modal.close(null);
  }

  public confirm(): void {
    this.value = this.normalizeCron(this.value);
    const cronValidation = validateCronScheduledSync(this.value);
    if (!cronValidation.valid) {
      this.validationError = cronValidation.message;
      return;
    }

    const timezoneValidation = validateCronScheduledSyncTimezone(this.selectedTimezone, this.value);
    if (!timezoneValidation.valid) {
      this.validationError = timezoneValidation.message;
      return;
    }

    this.modal.close({ cronExpression: this.value, timezone: this.selectedTimezone });
  }

  public disableAutomation(): void {
    this.modal.close({ cronExpression: 'None' });
  }

  ngOnInit(): void {
    if (this.initialCronExpression && this.initialCronExpression !== 'None') {
      this.value = this.initialCronExpression;
    }
    this.value = this.normalizeCron(this.value);
    this.selectedHour = this.getCronHour(this.value);
    this.selectedTimezone = resolveDefaultTimezone(this.initialTimezone);
  }

  public onCronChanged(expr: string): void {
    const normalized = this.normalizeCron(expr);
    if (normalized !== expr) {
      this.value = normalized;
    }
    this.selectedHour = this.getCronHour(this.value);
  }

  public onHourChanged(hour: number): void {
    this.value = this.setCronHour(this.value, hour);
    this.onCronChanged(this.value);
  }

  private getCronHour(expr: string): number {
    const parts = expr.trim().split(/\s+/);
    if (parts.length < 5) {
      return 0;
    }

    const hour = parts[1];
    if (hour.includes(',')) {
      return Number(hour.split(',')[0]) || 0;
    }

    return Number(hour) || 0;
  }

  private normalizeCron(expr: string): string {
    const parts = expr.trim().split(/\s+/);
    if (parts.length < 5) {
      return expr;
    }

    for (let i = 0; i < parts.length; i++) {
      if (parts[i] === '?') {
        parts[i] = '*';
      }
    }

    if (parts[0] !== '0') {
      parts[0] = '0';
    }

    const hour = parts[1];
    if (hour === '*' || hour.includes('/')) {
      parts[1] = '0';
    } else if (hour.includes('-')) {
      parts[1] = hour.split('-')[0] ?? '0';
    } else if (hour.includes(',')) {
      parts[1] = hour.split(',')[0] ?? '0';
    }

    return parts.join(' ');
  }

  private setCronHour(expr: string, hour: number): string {
    const parts = expr.trim().split(/\s+/);
    if (parts.length < 5) {
      return expr;
    }

    parts[1] = String(hour);
    return parts.join(' ');
  }
}
